<article class="doc">
  <header class="doc__header">
    <p class="doc__eyebrow">Atom</p>
    <h1 class="doc__title">Date Input</h1>
    <p class="doc__lead">
      Selector de fecha, fecha-hora y hora basado en Flatpickr. Misma API visual que
      <code>ui-input</code>: etiqueta, indicador obligatorio/opcional y estados accesibles.
      Formato <code>dd/mm/aaaa</code>. Localización en español. Máscara automática al escribir.
    </p>
  </header>

  <!-- ── Estados ─────────────────────────────── -->
  <h2 class="doc-section-title">Estados</h2>

  <div class="doc-grid">
    <app-example-section
      title="Normal · Obligatorio"
      description="Estado por defecto con campo requerido."
    >
      <ui-date-input
        [block]="true"
        label="Fecha de nacimiento"
        kind="date"
        [required]="true"
      />
    </app-example-section>

    <app-example-section
      title="Normal · Opcional"
      description="Sin indicador de obligatorio."
    >
      <ui-date-input
        [block]="true"
        label="Fecha de vencimiento"
        kind="date"
        [required]="false"
      />
    </app-example-section>

    <app-example-section
      title="Error / inválido"
      description="Borde rojo cuando la fecha es inválida."
    >
      <div class="doc-example-stack">
        <ui-date-input
          [block]="true"
          label="Fecha de contrato"
          kind="date"
          [required]="true"
          [invalid]="isInvalid()"
          [message]="isInvalid() ? 'La fecha no puede ser anterior a hoy.' : undefined"
        />
        <button type="button" class="doc-native-toggle" (click)="toggleInvalid()">
          {{ isInvalid() ? 'Quitar error' : 'Activar error' }}
        </button>
      </div>
    </app-example-section>

    <app-example-section
      title="Deshabilitado"
      description="Sin interacción; no abre el selector."
    >
      <ui-date-input
        [block]="true"
        label="Fecha de cierre"
        kind="date"
        [disabled]="true"
      />
    </app-example-section>

    <app-example-section
      title="Solo lectura"
      description="Muestra el valor sin permitir cambios."
    >
      <ui-date-input
        [block]="true"
        label="Fecha de alta"
        kind="date"
        value="15/01/2025"
        [readonly]="true"
      />
    </app-example-section>
  </div>

  <!-- ── Tipos ───────────────────────────────── -->
  <h2 class="doc-section-title">Tipos (UiDateInputKind)</h2>

  <div class="doc-grid doc-grid--2">
    <app-example-section
      title="date"
      description="Selector de fecha. Formato dd/mm/aaaa."
    >
      <ui-date-input
        [block]="true"
        label="Fecha"
        kind="date"
        [required]="true"
        [(value)]="dateValue"
      />
      @if (dateValue()) {
        <p class="doc-value-preview">Valor: {{ dateValue() }}</p>
      }
    </app-example-section>

    <app-example-section
      title="datetime"
      description="Selector de fecha y hora. Formato dd/mm/aaaa hh:mm."
    >
      <ui-date-input
        [block]="true"
        label="Fecha y hora"
        kind="datetime"
        [required]="true"
        [(value)]="datetimeValue"
      />
      @if (datetimeValue()) {
        <p class="doc-value-preview">Valor: {{ datetimeValue() }}</p>
      }
    </app-example-section>

    <app-example-section
      title="time"
      description="Selector de hora. Formato hh:mm en 24 h."
    >
      <ui-date-input
        [block]="true"
        label="Hora"
        kind="time"
        [required]="true"
        [(value)]="timeValue"
      />
      @if (timeValue()) {
        <p class="doc-value-preview">Valor: {{ timeValue() }}</p>
      }
    </app-example-section>
  </div>

  <!-- ── Ejemplos de uso ─────────────────────── -->
  <section class="doc-block">
    <h2 class="doc-block__title">Ejemplos de uso</h2>

    <p class="doc-block__p">Binding bidireccional con signal:</p>
    <pre class="doc-block__pre" role="code"><code>// component.ts
fechaInicio = signal('');
fechaCita  = signal('');
horaApertura = signal('');</code></pre>

    <p class="doc-block__p">Fecha:</p>
    <pre class="doc-block__pre" role="code"><code>&lt;ui-date-input
  label="Fecha de inicio"
  kind="date"
  [required]="true"
  [(value)]="fechaInicio"
/&gt;</code></pre>

    <p class="doc-block__p">Fecha y hora:</p>
    <pre class="doc-block__pre" role="code"><code>&lt;ui-date-input
  label="Fecha y hora de cita"
  kind="datetime"
  [required]="true"
  [(value)]="fechaCita"
/&gt;</code></pre>

    <p class="doc-block__p">Hora:</p>
    <pre class="doc-block__pre" role="code"><code>&lt;ui-date-input
  label="Hora de apertura"
  kind="time"
  [required]="true"
  [(value)]="horaApertura"
/&gt;</code></pre>

    <p class="doc-block__p">Con error y mensaje:</p>
    <pre class="doc-block__pre" role="code"><code>&lt;ui-date-input
  label="Fecha de contrato"
  kind="date"
  [required]="true"
  [invalid]="fechaInvalida()"
  message="La fecha no puede ser anterior a hoy."
  [(value)]="fechaContrato"
/&gt;</code></pre>

    <p class="doc-block__p">Con Reactive Forms (usando <code>valueChange</code>):</p>
    <pre class="doc-block__pre" role="code"><code>&lt;ui-date-input
  label="Fecha de nacimiento"
  kind="date"
  [required]="true"
  [invalid]="form.controls.fecha.invalid && form.controls.fecha.touched"
  [value]="form.controls.fecha.value ?? ''"
  (valueChange)="form.controls.fecha.setValue($event)"
/&gt;</code></pre>

    <p class="doc-block__p">Modo compacto (<code>[showLabel]="false"</code>) — ideal para filtros en <code>ui-title-bar</code>:</p>
    <pre class="doc-block__pre" role="code"><code>&lt;!-- En un ui-title-bar mode="report" --&gt;
&lt;ui-title-bar mode="report" title="Reporte de ventas" ...&gt;
  &lt;ui-date-input
    placeholder="Fecha inicio"
    [(value)]="dateFrom"
    [showLabel]="false"
  /&gt;
  &lt;ui-date-input
    placeholder="Fecha fin"
    [(value)]="dateTo"
    [showLabel]="false"
  /&gt;
&lt;/ui-title-bar&gt;</code></pre>
  </section>

  <!-- ── Inputs ─────────────────────────────── -->
  <section class="doc-block">
    <h2 class="doc-block__title">Inputs</h2>
    <table class="doc-table">
      <thead>
        <tr>
          <th>Nombre</th>
          <th>Tipo</th>
          <th>Defecto</th>
          <th>Descripción</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><code>kind</code></td>
          <td><code>'date' | 'datetime' | 'time'</code></td>
          <td><code>'date'</code></td>
          <td>Tipo de selector</td>
        </tr>
        <tr>
          <td><code>label</code></td>
          <td><code>string</code></td>
          <td><code>''</code></td>
          <td>Etiqueta visible sobre el campo</td>
        </tr>
        <tr>
          <td><code>value</code></td>
          <td><code>string</code> (model)</td>
          <td><code>''</code></td>
          <td>Valor del campo; soporta <code>[(value)]</code></td>
        </tr>
        <tr>
          <td><code>placeholder</code></td>
          <td><code>string</code></td>
          <td>Según <code>kind</code></td>
          <td>Texto de ayuda cuando el campo está vacío</td>
        </tr>
        <tr>
          <td><code>required</code></td>
          <td><code>boolean</code></td>
          <td><code>false</code></td>
          <td>Muestra etiqueta "Obligatorio" y aplica <code>aria-required</code></td>
        </tr>
        <tr>
          <td><code>showOptionalHint</code></td>
          <td><code>boolean</code></td>
          <td><code>true</code></td>
          <td>Muestra etiqueta "Opcional" cuando no es requerido</td>
        </tr>
        <tr>
          <td><code>requiredHint</code></td>
          <td><code>string</code></td>
          <td><code>'Obligatorio'</code></td>
          <td>Texto personalizable del indicador de campo requerido</td>
        </tr>
        <tr>
          <td><code>optionalHint</code></td>
          <td><code>string</code></td>
          <td><code>'Opcional'</code></td>
          <td>Texto personalizable del indicador de campo opcional</td>
        </tr>
        <tr>
          <td><code>invalid</code></td>
          <td><code>boolean</code></td>
          <td><code>false</code></td>
          <td>Activa el estado de error (borde rojo)</td>
        </tr>
        <tr>
          <td><code>message</code></td>
          <td><code>string | undefined</code></td>
          <td><code>undefined</code></td>
          <td>Texto de ayuda o error bajo el campo</td>
        </tr>
        <tr>
          <td><code>disabled</code></td>
          <td><code>boolean</code></td>
          <td><code>false</code></td>
          <td>Deshabilita el campo e impide abrir el selector</td>
        </tr>
        <tr>
          <td><code>readonly</code></td>
          <td><code>boolean</code></td>
          <td><code>false</code></td>
          <td>Muestra el valor pero impide cambios</td>
        </tr>
        <tr>
          <td><code>block</code></td>
          <td><code>boolean</code></td>
          <td><code>false</code></td>
          <td>El componente ocupa el 100% del ancho del contenedor</td>
        </tr>
        <tr>
          <td><code>name</code></td>
          <td><code>string | undefined</code></td>
          <td><code>undefined</code></td>
          <td>Atributo <code>name</code> del input nativo</td>
        </tr>
        <tr>
          <td><code>showLabel</code></td>
          <td><code>boolean</code></td>
          <td><code>true</code></td>
          <td>Oculta el bloque completo de label + badge cuando es <code>false</code>. Necesario al usar el campo en contextos compactos como filtros en <code>ui-title-bar</code>.</td>
        </tr>
        <tr>
          <td><code>fieldId</code></td>
          <td><code>string | undefined</code></td>
          <td>Auto-generado</td>
          <td>Sobrescribe el <code>id</code> del input y el <code>for</code> del label</td>
        </tr>
      </tbody>
    </table>
  </section>

  <!-- ── Outputs ────────────────────────────── -->
  <section class="doc-block">
    <h2 class="doc-block__title">Outputs</h2>
    <table class="doc-table">
      <thead>
        <tr>
          <th>Nombre</th>
          <th>Tipo</th>
          <th>Descripción</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><code>valueChange</code></td>
          <td><code>OutputEmitter&lt;string&gt;</code></td>
          <td>Emite el nuevo valor cada vez que cambia (ya sea por teclado o por el selector)</td>
        </tr>
        <tr>
          <td><code>blurred</code></td>
          <td><code>OutputEmitter&lt;FocusEvent&gt;</code></td>
          <td>Emite cuando el campo pierde el foco</td>
        </tr>
        <tr>
          <td><code>focused</code></td>
          <td><code>OutputEmitter&lt;FocusEvent&gt;</code></td>
          <td>Emite cuando el campo recibe el foco</td>
        </tr>
      </tbody>
    </table>
  </section>

  <!-- ── Buenas prácticas ───────────────────── -->
  <section class="doc-block">
    <h2 class="doc-block__title">Buenas prácticas</h2>
    <ul class="doc-list">
      <li>
        Usa <code>[(value)]</code> para binding bidireccional con signals.
        Usa <code>[value]</code> + <code>(valueChange)</code> para integrarlo con Reactive Forms.
      </li>
      <li>
        El valor del modelo siempre es un <code>string</code> con el formato configurado
        (<code>dd/mm/aaaa</code>, <code>dd/mm/aaaa hh:mm</code> o <code>hh:mm</code>).
        Convierte a <code>Date</code> en la feature, no en el átomo.
      </li>
      <li>
        El selector de Flatpickr se cierra automáticamente al hacer scroll en el contenedor
        padre para evitar desalineamiento visual.
      </li>
      <li>
        La máscara automática inserta los separadores (<code>/</code>, espacio, <code>:</code>)
        mientras el usuario escribe; no es necesario tipiarlos manualmente.
      </li>
      <li>
        Para campos de fecha en formularios de alta, prefiere <code>kind="date"</code>.
        Usa <code>kind="datetime"</code> solo cuando la hora es relevante para el negocio
        (citas, reservaciones, registros de eventos).
      </li>
      <li>
        Usa <code>[showLabel]="false"</code> cuando el campo se coloca en una barra de filtros
        (p. ej. dentro de <code>ui-title-bar</code>) para eliminar el espacio del encabezado y
        mantener la alineación vertical con otros controles (<code>ui-select</code>, <code>ui-multiselect</code>).
      </li>
      <li>
        No inyectar servicios en <code>UiDateInputComponent</code>: vive en
        <code>libs/shared/ui</code> y no debe tener dependencias de dominio.
      </li>
    </ul>
  </section>
</article>
